import React, { useEffect, useState } from "react";
import { message, Spin, Radio } from "antd";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import "./NewDiscordTrigger.css";
import { BotTemplates, TriggerCreate } from "@dots-projects/types";
import { persistTrigger, useTrigger } from "../TriggerEditor/useTrigger";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import ScratchTriggerForm from "./ScratchTriggerForm";
import TriggerTemplates from "./TriggerTemplates";
import TemplateDetails from "./TemplateDetails";
import { DiscordTrigger } from "../../lib/discordTypes";

function CopyTrigger({
  workspace,
  setNewTrigger,
  triggerId,
}: {
  workspace: string;
  setNewTrigger: React.Dispatch<React.SetStateAction<TriggerCreate | null>>;
  triggerId: string;
}) {
  const { data, isLoading } = useTrigger(triggerId, "live");

  return (
    <Spin spinning={isLoading} wrapperClassName={"trigger-option-container"}>
      <ScratchTriggerForm
        workspace={workspace}
        setNewTrigger={setNewTrigger}
        configuration={{
          steps: data?.trigger.steps!,
          triggerName: `${data?.trigger.name} (copy)`,
        }}
      />
    </Spin>
  );
}

function NewDiscordTrigger({
  backgroundPosition,
  botTemplates,
}: {
  backgroundPosition: string[];
  botTemplates: BotTemplates[];
}) {
  const navigate = useNavigate();
  //TODO: use enum types for consistency
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [newTrigger, setNewTrigger] = useState<TriggerCreate | null>(null);

  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const typeOfTrigger = params.get("type") || "";
  const activeTab = typeOfTrigger === "scratch" ? "scratch" : "templates";
  const copyTrigger = !!params.get("trigger_id") || null;

  const triggerDetailView =
    Boolean(typeOfTrigger) && !["templates", "scratch"].includes(typeOfTrigger);

  const createTrigger = async () => {
    if (newTrigger) {
      try {
        const guildId = localStorage.getItem("guild-id") as string;
        setLoading(true);
        const createdTrigger = await persistTrigger(newTrigger);
        message.success("Trigger created!");
        window.analytics.track("Initial trigger created by user");
        if (createdTrigger[0][0]) {
          navigate(`/automation/${createdTrigger[0][0].liveId}/edit`);
          queryClient.invalidateQueries([
            "discord-triggers",
            { guildId, workspace },
          ]);
        }
      } catch (e) {
        console.log("Error creating trigger", e);
        window.analytics.track("Error creating initial trigger", {
          errorInfo: JSON.stringify(e),
        });
        message.error("Error saving. Please try again");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    createTrigger();
  }, [newTrigger]);

  if (copyTrigger) {
    return (
      <CopyTrigger
        workspace={workspace}
        setNewTrigger={setNewTrigger}
        triggerId={params.get("trigger_id"!) as string}
      />
    );
  }

  return (
    <Spin spinning={loading} wrapperClassName={"trigger-option-container"}>
      {triggerDetailView ? (
        <TemplateDetails
          workspace={workspace}
          setNewTrigger={setNewTrigger}
          typeOfTrigger={typeOfTrigger}
          backgroundPosition={backgroundPosition}
          botTemplates={botTemplates}
        />
      ) : (
        <div>
          <div className="flex justify-center items-center">
            <Radio.Group
              onChange={(e) => {
                navigate(`/automation/new?type=${e.target.value}`);
              }}
              value={activeTab}
              style={{ marginBottom: 8 }}
            >
              <Radio.Button value="scratch">Start from scratch</Radio.Button>
              <Radio.Button value="templates">Templates</Radio.Button>
            </Radio.Group>
          </div>
          {activeTab === "scratch" ? (
            <ScratchTriggerForm
              workspace={workspace}
              setNewTrigger={setNewTrigger}
            />
          ) : (
            <TriggerTemplates
              backgroundPosition={backgroundPosition}
              botTemplates={botTemplates}
            />
          )}
        </div>
      )}
    </Spin>
  );
}
export default NewDiscordTrigger;
